/**
 * PointsHistory — a chronological log of every points event.
 *
 * Personal mode: any workspace member sees their own history.
 * Admin mode (owner/admin/points.rules.manage): a filterable, sortable
 * table view spanning every member with a "Personal" toggle to flip
 * between "just me" and "everyone".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { PersonChip } from "@/components/PersonChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import {
  History as HistoryIcon, Filter, Search, ArrowUpDown, TrendingUp, TrendingDown,
  X, Info, Trophy, Star, Download, Users2,
} from "lucide-react";

const SORTS = [
  { v: "newest", label: "Newest first" },
  { v: "oldest", label: "Oldest first" },
  { v: "highest", label: "Highest points" },
  { v: "lowest", label: "Lowest points" },
];

const SIGNS = [
  { v: "", label: "Any" },
  { v: "positive", label: "Positive (earned)" },
  { v: "negative", label: "Negative (deducted)" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function PointsHistory() {
  const { user, activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ event_kinds: [], sources: [], members: [], is_admin: false });
  const [loading, setLoading] = useState(true);

  // Filters
  const [mode, setMode] = useState("personal"); // "personal" | "admin"
  const [userId, setUserId] = useState("");     // admin-only
  const [eventKind, setEventKind] = useState("");
  const [source, setSource] = useState("");
  const [sign, setSign] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [qText, setQText] = useState("");
  const [sort, setSort] = useState("newest");
  const [offset, setOffset] = useState(0);
  const [inspect, setInspect] = useState(null);
  const PAGE = 50;

  const isAdminUser = !!facets.is_admin;
  const effectiveMode = isAdminUser ? mode : "personal";

  // Load facets (once)
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/points-ledger/facets`)
      .then((r) => setFacets(r.data || {}))
      .catch(() => setFacets({ event_kinds: [], sources: [], members: [] }));
  }, [wsId]);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (effectiveMode === "personal") {
        params.set("user_id", user?.id || "");
      } else if (userId) {
        params.set("user_id", userId);
      }
      if (eventKind) params.set("event_kind", eventKind);
      if (source) params.set("source", source);
      if (sign) params.set("sign", sign);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (qText) params.set("q_text", qText);
      params.set("sort", sort);
      params.set("limit", String(PAGE));
      params.set("offset", String(offset));
      const { data } = await api.get(`/workspaces/${wsId}/points-ledger?${params.toString()}`);
      const list = Array.isArray(data) ? data : (data?.rows || []);
      setRows(list);
      setTotal(Array.isArray(data) ? list.length : (data?.total || list.length));
    } catch (e) {
      toast.error("Failed to load history");
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [wsId, effectiveMode, userId, eventKind, source, sign, dateFrom, dateTo, qText, sort, offset, user?.id]);

  useEffect(() => { load(); }, [load]);

  // Reset offset when filters change
  useEffect(() => { setOffset(0); }, [effectiveMode, userId, eventKind, source, sign, dateFrom, dateTo, qText, sort]);

  const clearFilters = () => {
    setEventKind(""); setSource(""); setSign("");
    setDateFrom(""); setDateTo(""); setQText(""); setUserId("");
    setSort("newest"); setOffset(0);
  };

  const totalEarned = useMemo(() => rows.filter((r) => (r.points || 0) > 0).reduce((s, r) => s + (r.points || 0), 0), [rows]);
  const totalLost = useMemo(() => rows.filter((r) => (r.points || 0) < 0).reduce((s, r) => s + (r.points || 0), 0), [rows]);
  const netPoints = totalEarned + totalLost;

  const exportCsv = () => {
    const cols = ["When", "Member", "Points", "Event kind", "Source", "Label", "Reason", "Awarded by"];
    const lines = [cols.map(csvEscape).join(",")];
    for (const r of rows) {
      lines.push([
        r.created_at, r.user?.name || r.user_id,
        r.points, r.event_kind || "", r.source || "",
        r.source_label || "", r.reason || "",
        r.awarded_by_name || "",
      ].map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `points-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="points-history-page">
      <PageHeader
        title="Points history"
        icon={HistoryIcon}
        description={effectiveMode === "personal"
          ? "Your earned and deducted points, event-by-event."
          : "Every points event across the workspace. Filter, sort, inspect."}
        actions={(
          <div className="flex items-center gap-2">
            {isAdminUser && (
              <div className="inline-flex border border-border rounded-none overflow-hidden" data-testid="ph-mode-toggle">
                <button
                  type="button"
                  onClick={() => setMode("personal")}
                  className={`px-3 py-1.5 text-xs ${mode === "personal" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                  data-testid="ph-mode-personal"
                >
                  <span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Personal</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("admin")}
                  className={`px-3 py-1.5 text-xs ${mode === "admin" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                  data-testid="ph-mode-admin"
                >
                  <span className="inline-flex items-center gap-1"><Users2 className="w-3.5 h-3.5" /> Everyone</span>
                </button>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="ph-export-csv">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/leaderboard" data-testid="ph-open-leaderboard">
                <Star className="w-3.5 h-3.5 mr-1.5" /> Open leaderboard
              </Link>
            </Button>
          </div>
        )}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Events on page" value={rows.length} testid="ph-kpi-events" />
        <KpiCard label="Total in range" value={total} testid="ph-kpi-total" />
        <KpiCard
          label="Earned"
          value={`+${totalEarned}`}
          tone="pos"
          icon={TrendingUp}
          testid="ph-kpi-earned"
        />
        <KpiCard
          label="Deducted"
          value={String(totalLost)}
          tone="neg"
          icon={TrendingDown}
          testid="ph-kpi-lost"
        />
      </div>

      {/* Filters */}
      <div className="border border-border p-3 space-y-3" data-testid="ph-filters">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {effectiveMode === "admin" && (
            <div className="lg:col-span-2">
              <Label className="text-[10px] uppercase text-muted-foreground">Member</Label>
              <Select value={userId || "__all__"} onValueChange={(v) => setUserId(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="ph-filter-user">
                  <SelectValue placeholder="Everyone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Everyone</SelectItem>
                  {facets.members?.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.name} <span className="opacity-60 ml-1">· {m.points_total}pt</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Event kind</Label>
            <Select value={eventKind || "__all__"} onValueChange={(v) => setEventKind(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="ph-filter-kind"><SelectValue placeholder="All events" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All events</SelectItem>
                {facets.event_kinds?.map((k) => (
                  <SelectItem key={k.key} value={k.key}>{k.key} <span className="opacity-60 ml-1">· {k.count}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Source</Label>
            <Select value={source || "__all__"} onValueChange={(v) => setSource(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="ph-filter-source"><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sources</SelectItem>
                {facets.sources?.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.key} <span className="opacity-60 ml-1">· {s.count}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Sign</Label>
            <Select value={sign || "__any__"} onValueChange={(v) => setSign(v === "__any__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="ph-filter-sign"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                {SIGNS.map((s) => <SelectItem key={s.v || "__any__"} value={s.v || "__any__"}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 text-xs" data-testid="ph-filter-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 text-xs" data-testid="ph-filter-to" />
          </div>
          <div className="lg:col-span-2">
            <Label className="text-[10px] uppercase text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder="Search label or reason…"
                className="h-9 text-xs pl-8"
                data-testid="ph-filter-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Sort</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 text-xs" data-testid="ph-filter-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button size="sm" variant="outline" onClick={clearFilters} className="w-full h-9" data-testid="ph-clear-filters">
              <X className="w-3.5 h-3.5 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="p-6 text-sm text-muted-foreground" data-testid="ph-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={HistoryIcon}
          title="No matching events"
          description="Try widening the date range or clearing filters."
          testId="ph-empty"
        />
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="ph-table">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase text-muted-foreground">
                <th className="px-3 py-2">When</th>
                {effectiveMode === "admin" && <th className="px-3 py-2">Member</th>}
                <th className="px-3 py-2">Points</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Reason / label</th>
                <th className="px-3 py-2">Awarded by</th>
                <th className="px-3 py-2 w-14 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30" data-testid={`ph-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="font-medium">{fmtDate(r.created_at)}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtRelative(r.created_at)}</div>
                  </td>
                  {effectiveMode === "admin" && (
                    <td className="px-3 py-2">
                      <PersonChip person={r.user || { id: r.user_id }} size="sm" testid={`ph-user-${r.id}`} />
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono">
                    <span className={`inline-flex items-center px-1.5 py-0.5 border font-semibold ${
                      (r.points || 0) > 0
                        ? "text-emerald-700 bg-emerald-50 border-emerald-300"
                        : (r.points || 0) < 0
                          ? "text-rose-700 bg-rose-50 border-rose-300"
                          : "text-slate-700 bg-slate-50 border-slate-300"
                    }`} data-testid={`ph-points-${r.id}`}>
                      {(r.points || 0) > 0 ? "+" : ""}{r.points || 0}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs">{r.event_kind || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{r.source || "—"}</div>
                  </td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="truncate">{r.source_label || r.reason || "—"}</div>
                    {r.reason && r.source_label && (
                      <div className="text-[10px] text-muted-foreground truncate">{r.reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.awarded_by_name || "system"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setInspect(r)}
                      className="p-1 text-muted-foreground hover:text-foreground"
                      aria-label="Inspect"
                      data-testid={`ph-inspect-${r.id}`}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pager */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>Showing {offset + 1}–{offset + rows.length} of {total}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={offset <= 0}
                     onClick={() => setOffset(Math.max(0, offset - PAGE))}
                     data-testid="ph-prev">Previous</Button>
            <Button size="sm" variant="outline"
                     disabled={offset + rows.length >= total}
                     onClick={() => setOffset(offset + PAGE)}
                     data-testid="ph-next">Next</Button>
          </div>
        </div>
      )}

      <Sheet open={!!inspect} onOpenChange={(o) => !o && setInspect(null)}>
        <SheetContent side="right" className="w-[420px] sm:w-[520px]" data-testid="ph-inspect-drawer">
          <SheetHeader>
            <SheetTitle>Points event · {inspect ? fmtDate(inspect.created_at) : ""}</SheetTitle>
          </SheetHeader>
          {inspect && (
            <div className="mt-4 space-y-3 text-sm">
              <InspectRow label="Member" value={<PersonChip person={inspect.user || { id: inspect.user_id }} size="sm" />} />
              <InspectRow label="Points" value={
                <span className={`font-bold ${(inspect.points || 0) > 0 ? "text-emerald-700" : (inspect.points || 0) < 0 ? "text-rose-700" : ""}`}>
                  {(inspect.points || 0) > 0 ? "+" : ""}{inspect.points || 0}
                </span>
              } />
              <InspectRow label="Event kind" value={inspect.event_kind || "—"} />
              <InspectRow label="Source" value={inspect.source || "—"} />
              <InspectRow label="Label" value={inspect.source_label || "—"} />
              <InspectRow label="Reason" value={inspect.reason || "—"} />
              <InspectRow label="Awarded by" value={inspect.awarded_by_name || "system"} />
              <InspectRow label="Target type" value={inspect.target_type || "—"} />
              <InspectRow label="Target id" value={<code className="text-[10px] break-all">{inspect.target_id || "—"}</code>} />
              <InspectRow label="Created" value={fmtDate(inspect.created_at)} />
              {inspect.idempotency_key && (
                <InspectRow label="Idempotency key"
                             value={<code className="text-[10px] break-all">{inspect.idempotency_key}</code>} />
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}


function KpiCard({ label, value, tone, icon: Icon, testid }) {
  const toneCls = tone === "pos"
    ? "text-emerald-700 border-emerald-300 bg-emerald-50/50"
    : tone === "neg"
      ? "text-rose-700 border-rose-300 bg-rose-50/50"
      : "text-foreground border-border bg-muted/20";
  return (
    <div className={`border ${toneCls} p-3`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function InspectRow({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1.5 border-b border-border/50 last:border-b-0">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="text-xs break-words">{value}</div>
    </div>
  );
}
